import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useQueryClient } from '@tanstack/react-query';
import React, { FC } from 'react';
import { OrgQueryKeys } from '../../../../api/config';
import { useAddTravelTimes } from '../../../../api/mutations/reworked/add/use-add-travel-times';
import { useUser } from '../../../../api/queries/use-user';
import { stopsToOrganizationLocationIdPairs } from '../../../../utils/data-mapping';
import { findUnknownPairsInCache, TravelTimesCache, updatePointlessPairs } from '../../../travel-times/use-travel-times';
import { DraftsLoadsSaveButton } from '../../drafts-loads-table-cells/drafts-loads-save-button';
import { EditableStopAdd } from '../../drafts-loads-table-cells/stops/editable/editable-stop-add';
import { useDraftLoadContext } from '../../hooks/draft-load-context';
import { EditableStopTableRow } from './editable-stop-table-row';

interface EditableLoadStopsProps {
    showInlineSaveButton: boolean;
}

export const EditableLoadStops: FC<EditableLoadStopsProps> = (props) => {
    const { showInlineSaveButton } = props;
    const { editedLoad, onEditedLoadChanged, dragDropUnitStateChange } = useDraftLoadContext();
    const { organizationId } = useUser();
    const { mutate: addTravelTimes } = useAddTravelTimes(organizationId);
    const queryKey = OrgQueryKeys.resolve(organizationId, OrgQueryKeys.locationTravelTimes);
    const queryClient = useQueryClient();
    const travelTimesCache = queryClient.getQueryData<TravelTimesCache>(([queryKey]));

    const handleOnDragEnd = (event: DragEndEvent) => {
        const reorderedStops = [...editedLoad.stops];
        if (!event.over || typeof event.active.id === 'string' || typeof event.over.id === 'string') {
            return reorderedStops;
        }

        const dragIndex = event.active.id - 1;
        const hoverIndex = event.over.id - 1;
        const dragStop = editedLoad.stops[dragIndex];

        const updatedStops = reorderedStops.resortValue(dragIndex, hoverIndex, dragStop).map((stop, i) => ({
            ...stop,
            sequence: i + 1,
        }));

        // Adjust unit states if necessary, since unit states are determined by index
        dragDropUnitStateChange(dragIndex, hoverIndex);

        onEditedLoadChanged({
            ...editedLoad,
            stops: updatedStops,
        });

        // This section will update travel times across stops after they're reordered
        if (travelTimesCache) {
            const pairs = stopsToOrganizationLocationIdPairs(updatedStops);
            const unknowPairs = findUnknownPairsInCache(travelTimesCache, pairs);
            const filteredUnknowPairs = updatePointlessPairs(unknowPairs, travelTimesCache, queryClient, queryKey);

            if (filteredUnknowPairs.length) {
                addTravelTimes(unknowPairs);
            }
        }
    }

    return (
        <>
            <DndContext
                onDragEnd={handleOnDragEnd}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                collisionDetection={closestCenter}
            >
                <SortableContext
                    items={editedLoad.stops.map(x => x.sequence)}
                    strategy={verticalListSortingStrategy}
                >
                    {editedLoad.stops.map((stop, i) => {
                        return (
                            <EditableStopTableRow
                                // inline edit add/split has no stopId
                                key={stop.id || i}
                                stop={stop}
                                trailerId={editedLoad.trailerId}
                                index={i}
                            />
                        )
                    })}
                </SortableContext>
            </DndContext>
            <div
                className='lt-row-stop-item'
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: showInlineSaveButton ? '.5rem 1rem .5rem 0' : undefined,
                }}
            >
                <EditableStopAdd />
                {showInlineSaveButton && <DraftsLoadsSaveButton />}
            </div>
        </>
    )
}
